import { gql } from '@apollo/client';

const WORKSTATION_FIELDS = gql`
  fragment WorkStation on WindowsAsset {
    serialNumber
    manufacturer
    model
    hostName
    primaryIp
    gateway
    platform
    # agentVersion
    domain
    status
    tag
    sysUptime
    teamViewerIntegrated
    splashtopIntegrated
    cwcIntegrated
    assetAge
  }
`;

const GET_ASSET_LIST = gql`
  query($classId: ID!, $listInfo: ListInfoInput!) {
    getAssetList(classId: $classId, listInfo: $listInfo) {
      assets {
        assetId
        name
        client {
          name
        }
        site {
          name
        }
        assetClass {
          name
        }
        customFields(
          fields: [
            "model"
            "manufacturer"
            "serialNumber"
            "hostName"
            "gateway"
            "platform"
            "domain"
            "agentVersion"
            "status"
            "primaryIp"
            "warrantyExpiry"
            "primaryMac"
          ]
        )
      }
      listInfo {
        page
        pageSize
        totalCount
        hasMore
      }
    }
  }
`;

const GET_ASSET_LIST_V2 = gql`
  query($classId: ID!, $listInfo: ListInfoInput!) {
    getAssetListV2(classId: $classId, listInfo: $listInfo) {
      assets {
        assetId
        name
      }
      listInfo {
        page
        pageSize
        totalCount
        hasMore
      }
    }
  }
`;
const GET_NON_MONITOREDASSETLIST = gql`
  query($classId: ID!, $listInfo: ListInfoInput!) {
    getAssetListV2(classId: $classId, listInfo: $listInfo) {
      assets {
        assetId
        name
      }
      listInfo {
        page
        pageSize
        totalCount
        hasMore
      }
    }
  }
`;
const GET_WORKSTATION_LIST = gql`
  query($listInfo: ListInfoInput!) {
    getWorkStationList(listInfo: $listInfo) {
      assets {
        assetId
        name
        client {
          name
          accountId
        }
        site {
          name
          id
        }
        owner {
          name
        }
        assetClass {
          name
          classId
        }
        ... on WorkStation {
          __typename
          serialNumber
          manufacturer
          model
          hostName
          primaryIp
          gateway
          platform
          domain
          status
          tag
          sysUptime
          teamViewerIntegrated
          splashtopIntegrated
          cwcIntegrated
          primaryMac
          assetAge
          registeredTime
          lastReportedTime
          patchStatus
          bitLockerStatus
          warrantyExpiryDate
          purchasedDate
          serialKey
          timeZone
        }
      }
      listInfo {
        page
        pageSize
        totalCount
        hasMore
      }
    }
  }
`;

const GET_ASSET_GROUP_V2 = gql`
  query($groupId: ID!, $listInfo: ListInfoInput!) {
    getAssetGroupAssetsV2(groupId: $groupId, listInfo: $listInfo) {
      assets {
        assetId
        name
        client {
          name
        }
        site {
          name
        }
        owner {
          name
        }
        assetClass {
          name
          classId
        }
        ... on WorkStation {
          __typename
          serialNumber
          manufacturer
          model
          hostName
          primaryIp
          gateway
          platform
          domain
          status
          tag
          sysUptime
          teamViewerIntegrated
          splashtopIntegrated
          cwcIntegrated
          primaryMac
          assetAge
          lastReportedTime
          patchStatus
          bitLockerStatus
          warrantyExpiryDate
          purchasedDate
          serialKey
          timeZone
          client {
            accountId
          }
          remoteDesktopEnabledStatus
        }
      }
      listInfo {
        page
        pageSize
        totalCount
        hasMore
      }
    }
  }
`;

const GET_ASSET = gql`
  query($assetId: ID!, $classId: ID!) {
    getAsset(assetId: $assetId, classId: $classId) {
      assetId
      name
      associatedAsset
      comment
      client {
        name
        accountId
      }
      owner {
        userId
        name
      }
      site {
        name
        id
      }
      assetClass {
        name
      }
      customFields(
        fields: [
          "model"
          "manufacturer"
          "serialNumber"
          "hostName"
          "gateway"
          "platform"
          "domain"
          "agentVersion"
          "status"
          "primaryIp"
          "warrantyExpiry"
          "primaryMac"
          "sysUptime"
          "status"
          "teamViewerIntegrated"
          "splashtopIntegrated"
          "cwcIntegrated"
        ]
      )
    }
  }
`;

const GET_CLASS_ID_FOR_ASSET = gql`
  query($assetId: ID!) {
    getAssetV2(assetId: $assetId) {
      assetId
      assetClass {
        name
        classId
      }
    }
  }
`;

const GET_BATTERY_DETAILS = gql`
  query($assetId: ID!) {
    getAssetBatteryDetails(assetId: $assetId) {
      index
      state
      currentCapacity
      voltage
      designVoltage
      chargeRate
      usage
    }
  }
`;

const GET_CLASS_FOR_ASSET_DETAILS = gql`
  query($assetId: ID!) {
    getAssetV2(assetId: $assetId) {
      assetId
      monitoringStatus
      policyCategory {
        name
      }
      assetClass {
        name
        classId
        moduleType
        entityName
        supportedModules
      }
      client {
        accountId
        name
        policyMigrationStatus
      }
    }
  }
`;

const GET_ASSET_ATTACHMENTS = gql`
  query($assetId: ID!) {
    getAssetAttachments(assetId: $assetId) {
      attachmentId
      name
    }
  }
`;

const GET_NON_MONITORED_ASSET_ATTACHMENTS = gql`
  query($assetId: ID!) {
    getNonMonitoredAssetAttachments(assetId: $assetId) {
      attachmentId
      name
    }
  }
`;

const GET_ASSET_TOP_CPU_PROCESS = gql`
  query($assetId: ID!) {
    getAssetTOPCPUProcessV2(assetId: $assetId) {
      assetId
      processId
      cmd
      threads
      percent
    }
  }
`;

const GET_ASSET_CPU_DETAILS = gql`
  query($assetId: ID!) {
    getAssetCPUDetailsV2(assetId: $assetId) {
      assetId
      cpuName
      currentSpeed
      maxSpeed
      physicalCore
      logicalCore
      architecture
      l1Cache
      l2Cache
      l3Cache
      processCount
      threadsCount
      handlesCount
      cpuUsage
      cpuCount
    }
  }
`;

const GET_ASSET_MEMORY_DETAILS = gql`
  query($assetId: ID!) {
    getAssetMemoryDetailsV2(assetId: $assetId) {
      totalMemory
      usedMemory
      availableMemory
      cachedMemory
      pagedPoolByte
      nonPagedPoolByte
      memUsage
      committedMemory
      swapTotalMem
      swapAvailableMem
      swapUsedMem
      totalRamSlots
      availableRamSlots
      usedRamSlots
      usableMemory
    }
  }
`;

const GET_ASSET_TOP_MEMORY_PROCESS = gql`
  query($assetId: ID!) {
    getAssetTopMemoryProcessV2(assetId: $assetId) {
      processId
      cmd
      commit
      workset
      sharable
      private
    }
  }
`;

const GET_ASSET_DISK_DETAILS = gql`
  query($assetId: ID!, $isWindows: Boolean!) {
    getAssetDiskDetailsV2(assetId: $assetId) {
      drive
      discType
      fileSystem
      size
      freeSize
      driveUsage

      maxFileLength @include(if: $isWindows)
      autoMounted @include(if: $isWindows)
      compressed @include(if: $isWindows)
      pageFile @include(if: $isWindows)
      indexed @include(if: $isWindows)
      activeTime @include(if: $isWindows)
      responseTime @include(if: $isWindows)
      readSpeed @include(if: $isWindows)
      writeSpeed @include(if: $isWindows)
    }
  }
`;

const GET_ASSET_USER_LOG = gql`
  query($assetId: ID!) {
    getAssetUserLog(assetId: $assetId) {
      name
      lastLoginTime
    }
  }
`;

const GET_ASSET_OPEN_PORTS = gql`
  query($assetId: ID!, $listInfo: ListInfoInput!) {
    getAssetOpenPorts(assetId: $assetId, listInfo: $listInfo) {
      openPorts {
        port
        pid
        status
        service
        protocol
      }
      listInfo {
        page
        pageSize
        totalCount
        hasMore
        sort {
          attribute
          order
        }
      }
    }
  }
`;

const GET_ASSET_SERVICE = gql`
  query GetAssetServiceInventory(
    $assetId: ID!
    $listInfo: ListInfoInput!
    $isWindows: Boolean!
  ) {
    getAssetServiceInventory(assetId: $assetId, listInfo: $listInfo) {
      services {
        service {
          serviceId
          name
          description
        }
        status
        startType @include(if: $isWindows)
        logonAs @include(if: $isWindows)
      }
      listInfo {
        page
        pageSize
        totalCount
        sort {
          attribute
          order
        }
      }
    }
  }
`;
const GET_ASSET_SERVICE_REDIS = gql`
  query GetAssetServiceInventory($assetId: ID!) {
    getAssetServiceInventoryV2(assetId: $assetId) {
      service {
        serviceId
        name
        description
      }
      status
      startType
      logonAs
    }
  }
`;
const GET_ASSET_ALL_PROCESS = gql`
  query($assetId: ID!) {
    getAssetAllCPUProcess(assetId: $assetId) {
      pid
      processName
      memUsage
      memByte
      cpuUsage
    }
  }
`;

const GET_ASSET_EVENTS = gql`
  query($assetId: ID!, $listInfo: ListInfoInput!) {
    getAssetEvents(assetId: $assetId, listInfo: $listInfo) {
      events {
        eventId
        message
        eventCode
        evtSource {
          sourceId
          name
        }
        eventType
        timeGenerated
        logName
      }
      listInfo {
        page
        pageSize
        hasMore
        sort {
          attribute
          order
        }
        nextPageKey
        prevPageKey
        totalCount
      }
    }
  }
`;

const GET_ASSET_INTERFACE = gql`
  query($assetId: ID!, $isSupported: Boolean!) {
    getAssetInterfaceV2(assetId: $assetId) {
      name
      mac
      ipv4Address
      ipv6Address
      comment
      infIndex
      mtu
      connectType
      lineSpeed @include(if: $isSupported)
      dataInPerSec
      dataOutPerSec
      adapterName
      status
    }
  }
`;

const GET_ASSET_HARDWARE_AND_MISC = gql`
  query($assetId: ID!, $isLinux: Boolean!) {
    getHardwareAndMiscV2(assetId: $assetId) {
      hardDriveInfo {
        id
        model
        manufacturer
        mediaType
        serialNumber
        driveSize
        driveInterface
        partitions
        bytesPerSector
        status

        busPath @include(if: $isLinux)
        storageController @include(if: $isLinux)
        driveType @include(if: $isLinux)
        removable @include(if: $isLinux)
        numaNodeId @include(if: $isLinux)
        driveName @include(if: $isLinux)
        vendor @include(if: $isLinux)

        diskPartition {
          size
          diskIndex
          partitionIndex
          bootable
          bootPartition
          primaryPartition
          partitionName @include(if: $isLinux)
          partitionType @include(if: $isLinux)
          uuid @include(if: $isLinux)
          label @include(if: $isLinux)
          mountedPoint @include(if: $isLinux)
          writable @include(if: $isLinux)
        }
      }

      tpmInfo {
        tpmVersion
        tpmStatus
      }

      biosInfo {
        biosInstallDate
        biosVersion
      }

      soundCard {
        manufacturer
        name
      }

      videoController {
        processor
        memory
        compatibility
        driverVersion
        installedDate
        resolution
      }

      pageInfo {
        name
        allocatedSize
        installedDate
        tempPageFile
      }
    }
  }
`;

const GET_ASSET_HARDWARE_AND_MISC_MAC = gql`
  query($assetId: ID!) {
    getHardwareAndMiscV2(assetId: $assetId) {
      diskPartitionInfo {
        diskIndex
        diskName
        mediaName
        diskConnection
        diskSchema
        smartStatus
        location
        solidState
        size
        diskPartition {
          bootable
          size
          partitionName
          mountedPoint
          deviceNode
          owners
          partitionType
          partitionConnection
          writable
          encrypted

          fileVault

          availableSize

          usedSize
        }
      }
    }
  }
`;

const GET_ASSET_SOFTWARES = gql`
  query($assetId: ID!, $listInfo: ListInfoInput!) {
    getAssetSoftwareInventory(assetId: $assetId, listInfo: $listInfo) {
      assetSoftwareInventories {
        id
        assetId
        version
        software {
          softwareId
          name
          manufacturer
        }
        installedDate
        bitVersion
        installedPath
        uninstallable
      }
      listInfo {
        page
        pageSize
        totalCount
        condition
        sort {
          attribute
          order
        }
      }
    }
  }
`;
const DELETE_SOFTWARE = gql`
  mutation($assetId: ID!, $assetSoftwareInventoryId: ID!) {
    deleteAssetSoftwareInventory(
      assetId: $assetId
      assetSoftwareInventoryId: $assetSoftwareInventoryId
    )
  }
`;

const GET_ASSET_SUMMARY = gql`
  query($assetId: ID!, $isWindowsAsset: Boolean!) {
    getAssetSummaryV2(assetId: $assetId) {
      cpu {
        assetId
        cpuName
        currentSpeed
        maxSpeed
        physicalCore
        logicalCore
        architecture
        l1Cache
        l2Cache
        l3Cache
        processCount
        threadsCount
        handlesCount
        cpuUsage
      }
      memory {
        totalMemory
        usedMemory
        availableMemory
        cachedMemory
        pagedPoolByte
        nonPagedPoolByte
        memUsage
        swapTotalMem
        swapAvailableMem
        swapUsedMem
      }
      disk {
        disks {
          drive
          discType
          fileSystem
          maxFileLength
          autoMounted
          compressed
          pageFile
          indexed
          size
          freeSize
          activeTime
          responseTime
          readSpeed
          writeSpeed
          driveUsage
          bitlockerInfo @include(if: $isWindowsAsset) {
            volumeStatus
            protStatus
          }
        }
        totalFreeSpace
        totalSize
      }
      assetInterface {
        name
        mac
        ipv4Address
        ipv6Address
        comment
        infIndex
        mtu
        connectType
        lineSpeed
        dataInPerSec
        dataOutPerSec
        adapterName
      }
      lastUserLog {
        name
        lastLoginTime
      }

      assetFields {
        sysUptime
      }
    }
  }
`;

const GET_ALL_ASSET_CLASS = gql`
  query {
    getAllAssetClass {
      classId
      name
      lastUpdatedTime
      description
      metrics {
        metricId
        name
        description
        pollInterval
        state
        category
      }
    }
  }
`;

const GET_ASSET_GROUP_META = gql`
  query {
    getAssetGroupMetaInfo
  }
`;

const GET_ASSET_GROUP_INFO_BY_CLASSID = gql`
  query($classId: ID!) {
    getAssetGroupMetaInfoByClassId(classId: $classId)
  }
`;

const GET_POLICIES_BY_CRITERIA = gql`
  query($listInfo: ListInfoInput!) {
    getPolicyListByCriteria(listInfo: $listInfo) {
      policyId
      name
    }
  }
`;

const GET_POLICY_TYPES = gql`
  query {
    getPolicyTypes
  }
`;

const ADD_ASSET_GROUP = gql`
  mutation($input: AssetGroupInput!) {
    createAssetGroup(input: $input) {
      groupId
      name
      policySet {
        policySetId
      }
      groupCondition
    }
  }
`;

const UPDATE_ASSET_GROUP = gql`
  mutation($groupId: ID!, $input: AssetGroupInput!) {
    updateAssetGroup(groupId: $groupId, input: $input) {
      groupId
      name
      groupCondition
      policySet {
        policySetId
        name
      }
    }
  }
`;

const GET_ASSET_GROUP = gql`
  query($groupId: ID!) {
    getAssetGroup(groupId: $groupId) {
      groupId
      name
      groupCondition
      assetClass {
        classId
      }
      policySet {
        policySetId
        name
      }
    }
  }
`;

const DELETE_ASSET_GROUP = gql`
  mutation($groupId: ID!) {
    deleteAssetGroup(groupId: $groupId)
  }
`;

const GET_ASSET_GROUP_LIST = gql`
  query($groupId: ID!, $listInfo: ListInfoInput!) {
    getAssetGroupAssets(groupId: $groupId, listInfo: $listInfo) {
      assets {
        assetId
        name
        client {
          name
        }
        site {
          name
        }
      }
      listInfo {
        page
      }
    }
  }
`;

const GET_ASSET_GROUP_VIEW_LIST = gql`
  query {
    getAllAssetGroups {
      groupId
      name
      groupCondition
      assetClass {
        classId
      }
      policySet {
        policySetId
        name
      }
    }
  }
`;

const GET_ASSET_GROUP_BY_ASSET_CLASS = gql`
  query($classId: ID!) {
    getAssetGroupsByAssetClass(classId: $classId) {
      groupId
      name
      groupCondition
      policySet {
        policySetId
        name
      }
      assetClass {
        classId
        name
      }
    }
  }
`;

const GET_ACTIVITY_FOR_ASSET = gql`
  query($assetId: ID!, $listInfo: ListInfoInput!) {
    getActivityForAsset(assetId: $assetId, listInfo: $listInfo) {
      activities {
        activityId
        module
        activityType
        activityData
        createdTime
        createdBy {
          name
        }
      }
      listInfo {
        totalCount
        page
      }
    }
  }
`;

const GET_TICKETS_FOR_ASSET = gql`
  query($listInfo: ListInfoInput!) {
    getTicketList(listInfo: $listInfo) {
      tickets {
        displayId
        priority
        ticketType
        ticketId
        subject
        status
        createdTime
        technician {
          userId
          firstName
          lastName
          name
          email
        }
        requester {
          userId
          firstName
          lastName
          name
          email
        }
        client {
          name
          accountId
        }
        resolutionDueTimeLeft
        resolutionDueTime
        resolutionTime
        resolutionViolated
        resolutionPauseTime
        totalTasks
        completedTasks
        latestReply {
          type
          user {
            userId
            email
          }
        }
      }
      listInfo {
        totalCount
        sort {
          attribute
          order
        }
        page
        pageSize
      }
    }
  }
`;

const GET_PATCHES_LIST = gql`
  query($listInfo: ListInfoInput!) {
    getPatchList(listInfo: $listInfo) {
      patches {
        patchId
        patchKey
        title
        publishedDate
        category
        severity
        approvalStatus
        totalDeviceCount
        installedDeviceCount
        failedDeviceCount
        kbNumbers {
          kbNumber
        }
        failedPercent
        version
        size
        assetClass {
          classId
        }
      }
      listInfo {
        page
        pageSize
        hasMore
        totalCount
        sort {
          attribute
          order
        }
      }
    }
  }
`;

const GET_ASSET_PATCH_SCHEDULE_ACTION_INFO = gql`
  query($assetId: ID!) {
    getAssetPatchScheduleActionInfo(assetId: $assetId) {
      lastPatchInstallAction {
        executionTime
        status
        statusMessage
        completedTime
      }
      nextPatchInstallAction {
        executionTime
      }
      nextPatchScanAction {
        executionTime
      }
      lastPatchScanAction {
        status
        executionTime
        statusMessage
        completedTime
      }
    }
  }
`;

const GET_PATCHES_FOR_ASSET = gql`
  query($assetId: ID!, $listInfo: ListInfoInput!, $fetchHistoryPatch: Boolean) {
    getAssetPatchList(
      assetId: $assetId
      listInfo: $listInfo
      fetchHistoryPatch: $fetchHistoryPatch
    ) {
      assetPatches {
        patchDetail {
          patchId
          patchKey
          title
          publishedDate
          category
          severity
          approvalStatus
          totalDeviceCount
          installedDeviceCount
          failedDeviceCount
          kbNumbers {
            kbNumber
          }
          maxCVSSDetail {
            cveNumber
            cvssScore
            severity
          }
          failedPercent
          size
          version
        }
        discoveredTime
        rollBack
        approvalStatus
        installationTime
        installationStatus
        failedMessage
        approvalTime
        approvalByDetails
        errorCode
      }
      listInfo {
        page
        pageSize
        hasMore
        totalCount
        sort {
          attribute
          order
        }
      }
    }
  }
`;

const GET_ANTIVIRUS_FOR_ASSET = gql`
  query($assetId: ID!) {
    getAssetAntiVirusDetail(assetId: $assetId) {
      antiVirusDetail {
        id
        name
      }
      isEnabled
      isUpToDate
      isIntegratedAV
      integrationRefId
      avStatus
    }
  }
`;

const GET_ALERTS_FOR_ASSET = gql`
  query($assetId: ID!, $listInfo: ListInfoInput!) {
    getAlertsForAsset(assetId: $assetId, listInfo: $listInfo) {
      alerts {
        message
        createdTime
        id
        status
        description
        severity
        resolvedTime
        occurrenceCount
        externalSource
        asset {
          name
          assetId
          assetClass {
            classId
          }
          client {
            name
            accountId
          }
          site {
            name
            id
          }
        }
        policy {
          name
          policyType
        }
      }
      listInfo {
        page
        pageSize
        totalCount
        condition
        sort {
          attribute
          order
        }
      }
    }
  }
`;

// IT DOCUMENTATION

const GET_NON_MONITORED_ASSET_CLASS_LIST = gql`
  query($clientId: ID) {
    getNonMonitoredAssetClassList(clientId: $clientId) {
      classId
      name
      isNonMonitored
      entityName
      lastUpdatedTime
      isSystemGenerated
      assetsCount
    }
  }
`;

const GET_TOTP_FIELD_VALUE = gql`
  query($input: SecureFieldValueInput!) {
    getTotpFieldValue(input: $input)
  }
`;
const GET_SHARED_NON_MONITORED_ASSET_CLASS_LIST = gql`
  query {
    getSharedNonMonitoredAssetClassList {
      classId
      name
    }
  }
`;
const GET_ALL_SHARED_IT_DOCUMENTS = gql`
  query($listInfo: ListInfoInput!) {
    getAllITDocs(listInfo: $listInfo) {
      assets {
        assetId
        name
        assetClass {
          classId
          name
          entityName
        }
      }
      listInfo {
        page
        pageSize
        hasMore
        totalCount
        sort {
          attribute
          order
        }
      }
    }
  }
`;
const GET_ASSET_LIST_TO_ASSOCIATE_V2 = gql`
  query($assetId: ID!, $entityToAssociate: String!, $listInfo: ListInfoInput!) {
    getAssetListToAssociateV2(
      assetId: $assetId
      entityToAssociate: $entityToAssociate
      listInfo: $listInfo
    ) {
      listInfo {
        page
        pageSize
        hasMore
        totalCount
      }
      assets {
        assetId
        name
        monitoringStatus
        client {
          accountId
          name
        }
        site {
          id
          name
        }
        assetClass {
          name
          classId
        }
      }
    }
  }
`;
const GET_NON_MONITORED_ASSET_LIST_TO_ASSOCIATE = gql`
  query($assetId: ID!, $entityToAssociate: String!, $listInfo: ListInfoInput!) {
    getNonMonitoredAssetListToAssociate(
      assetId: $assetId
      entityToAssociate: $entityToAssociate
      listInfo: $listInfo
    ) {
      listInfo {
        page
        pageSize
        hasMore
        totalCount
      }
      assets {
        assetId
        name
        client {
          name
        }
        site {
          name
        }
        assetClass {
          name
          classId
        }
        ... on WorkStation {
          __typename
          serialNumber
          manufacturer
          model
          hostName
          primaryIp
          gateway
          platform
          domain
          status
          tag
          sysUptime
          teamViewerIntegrated
          splashtopIntegrated
          cwcIntegrated
          primaryMac
        }
      }
    }
  }
`;

const GET_ASSOCIATED_ASSET_CLASS_LIST_FOR_ASSET = gql`
  query($assetId: ID!) {
    getAssociatedAssetClassListForAsset(assetId: $assetId) {
      classId
      name
      entityName
      customFields {
        id
        label
        fieldType
        columnName
        options
      }
      assetDefaultFields
    }
  }
`;

const GET_ASSET_CLASS_LIST = gql`
  query($moduleType: String) {
    getAssetClassList(moduleType: $moduleType) {
      classId
      name
      lastUpdatedTime
      entityName
      isNonMonitored
      isSystemGenerated
      iconClass
      isGated
      status
    }
  }
`;

const GET_ASSET_CLASS_LIST_INFO = gql`
  query GetAssetClassListInfo(
    $listInfo: ListInfoInput!
    $input: AssetClassListInfoInput
  ) {
    getAssetClassListInfo(listInfo: $listInfo, input: $input) {
      listInfo {
        page
        pageSize
        hasMore
        totalCount
        condition
        sort {
          attribute
          order
        }
      }
      assetClass {
        classId
        name
        lastUpdatedTime
        entityName
        iconClass
        status
        isGated
        assetClassModule {
          module
        }
      }
    }
  }
`;

const GET_MONITORED_ASSET_CLASS_LIST = gql`
  query {
    getMonitoredAssetClassList {
      classId
      name
      iconClass
    }
  }
`;

const GET_WORKSTATION_ASSET_CLASS_LIST = gql`
  query {
    getWorkStationAssetClassList {
      classId
      name
    }
  }
`;

const GET_ENTITY_META = gql`
  query($entityName: String) {
    getEntityMeta(entityName: $entityName) {
      name
      label
      attributes {
        name
        label
        attributePlaceHolder {
          key
          label
        }
        filterType
        isIdentifier
        isKeyAttribute
        dataType
        entity
        uiDataType
        conditionPlaceHolders {
          key
          label
        }
        isCustom
        isDependent
        nestedAttributes
        showToClient
        autoComplete
        useInMetric
        metricFunctions
        labelInMetric
        useInGroupBy
        useInCondition
        conditionPlaceHoldersMeta
      }
    }
  }
`;

const CREATE_ASSET = gql`
  mutation($input: AssetInput!) {
    createAsset(input: $input) {
      assetId
    }
  }
`;

const UPDATE_ASSET = gql`
  mutation($assetId: ID!, $input: AssetInput!, $isMSPProduct: Boolean!) {
    updateAsset(assetId: $assetId, input: $input) {
      assetId
      name
      site {
        id
        name
      }
      client @include(if: $isMSPProduct) {
        accountId
        name
      }
      department @skip(if: $isMSPProduct) {
        businessFunctionId
        name
      }
      owner {
        name
        userId
      }
    }
  }
`;

const GET_ASSET_META_INFO = gql`
  query($classId: ID!) {
    getAssetMetaInfoV2(classId: $classId)
  }
`;

const GET_ASSET_META_INFO_V4 = gql`
  query($classId: ID!) {
    getAssetMetaInfoV4(classId: $classId)
  }
`;

const GET_WORK_STATION_META_INFO_V4 = gql`
  query {
    getWorkStationMetaInfoV4
  }
`;

const GET_ASSET_PATCH_STATUS = gql`
  query($assetId: ID!) {
    getAssetPatchStatus(assetId: $assetId)
  }
`;

const GET_ASSET_ACRONIS_STATUS = gql`
  query($assetId: ID!) {
    getAcronisEndpointDetails(assetId: $assetId)
  }
`;

const GET_ASSET_DATA_BACKUP_DETAIL = gql`
  query($assetId: ID!) {
    getAssetDataBackupDetail(assetId: $assetId) {
      name
      integrationId
      integrationRefId
      isIntegratedDataBackup
      additionalInfo
    }
  }
`;

const GET_ASSET_CLASS = gql`
  query($classId: ID!) {
    getAssetClass(classId: $classId) {
      classId
      policySet {
        policySetId
        name
      }
      name
      formMeta
      customFields {
        id
        label
        fieldType
        columnName
        options
      }
      assetDefaultFields
      fieldEntity {
        entityId
        name
      }
    }
  }
`;

const GET_ASSET_CLASS_FOR_ASSET = gql`
  query($assetId: ID!) {
    getAssetClassForAsset(assetId: $assetId) {
      classId
      name
      formMeta
      customFields {
        id
        label
        fieldType
        columnName
        options
      }
      assetDefaultFields
    }
  }
`;

const GET_ASSET_CLASS_FOR_NON_MONITOR_ASSET = gql`
  query($assetId: ID!) {
    getAssetClassForAsset(assetId: $assetId) {
      classId
      name
      formMeta
      customFields {
        id
        label
        fieldType
        columnName
        options
      }
      assetDefaultFields
    }
  }
`;

const GET_ASSET_CLASS_V1 = gql`
  query($classId: ID!) {
    getAssetClass(classId: $classId) {
      classId
      name
      formMeta
      customFields {
        id
        label
        fieldType
        columnName
        options
      }
      assetDefaultFields
    }
  }
`;
const GET_ASSET_CLASS_V1_CUSTOM_FIELDS = gql`
  query($classId: ID!) {
    getAssetClass(classId: $classId) {
      classId
      name
      customFields {
        id
        label
        fieldType
        columnName
        options
      }
    }
  }
`;
const GET_NON_MONITORED_ASSET_CLASS = gql`
  query($classId: ID!) {
    getNonMonitoredAssetClass(classId: $classId) {
      classId
      name
      formMeta
      customFields {
        id
        label
        fieldType
        columnName
        options
      }
      assetDefaultFields
    }
  }
`;
const GET_SHARED_ASSET_CLASS = gql`
  query($classId: ID!) {
    getSharedAssetClass(classId: $classId) {
      classId
      name
      formMeta
      customFields {
        id
        label
        fieldType
        columnName
        options
      }
      assetDefaultFields
    }
  }
`;
const GET_ASSET_LIST_TO_ASSOCIATE = gql`
  query($assetId: ID!, $entityName: String, $listInfo: ListInfoInput) {
    getAssetListToAssociate(
      assetId: $assetId
      entityToAssociate: $entityName
      listInfo: $listInfo
    ) {
      assets {
        assetId
        name
        customFields
      }
    }
  }
`;

const ASSOCIATE_NON_MONITORED_ASSET = gql`
  mutation($assetId: ID!, $relatedAsset: RelatedAssetInput!) {
    associateAsset(assetId: $assetId, relatedAsset: $relatedAsset)
  }
`;

const CREATE_USER_ACTION_CONFIG = gql`
  mutation($assetId: ID!, $input: UserActionConfigInput) {
    createUserActionConfig(assetId: $assetId, input: $input) {
      actionConfigId
      script {
        scriptId
      }
      scriptArguments
      addedBy {
        name
      }
      cancelledBy {
        name
      }
      cancelledTime
      cancelled
      scheduleDetail
      executionType
    }
  }
`;
const BULK_CREATE_USER_ACTION_CONFIG = gql`
  mutation($assetIds: [ID]!, $input: UserActionConfigInput) {
    bulkCreateUserActionConfig(assetIds: $assetIds, input: $input) {
      assetId
      userActionConfig {
        actionConfigId
        script {
          scriptId
        }
        scriptArguments
        addedBy {
          name
        }
        cancelledBy {
          name
        }
        cancelledTime
        cancelled
        scheduleDetail
        executionType
      }
    }
  }
`;

const BULK_CREATE_USER_ACTION_CONFIG_V2 = gql`
  mutation($input: BulkCreateUserActionConfigInput!) {
    bulkCreateUserActionConfigV2(input: $input)
  }
`;

const REMOVE_ASSOCIATION = gql`
  mutation($assetId: ID!, $relatedAsset: RelatedAssetInput!) {
    removeAssociation(assetId: $assetId, relatedAsset: $relatedAsset)
  }
`;

const DELETE_NON_MONITORED_ASSET = gql`
  mutation($assetId: ID!) {
    deleteAsset(assetId: $assetId)
  }
`;

const GET_ASSET_UPCOMING_SCRIPTS = gql`
  query($assetId: ID!, $listInfo: ListInfoInput) {
    getAssetSchedulerDetailsListV2(assetId: $assetId, listInfo: $listInfo) {
      schedulerDetails {
        assetId
        actionConfig {
          actionConfigId
          skipOnOffline
          script {
            scriptId
            name
            language
            custom
          }
          scheduleDetail
          scriptArguments
          executionType
          policy {
            policySet {
              policySetId
            }
            policyId
            name
          }
        }

        nextExecutionTime
      }
      listInfo {
        page
        pageSize
        totalCount
        sort {
          attribute
          order
        }
      }
    }
  }
`;

const GET_EXECUTED_SCRIPTS = gql`
  query($assetId: ID!, $listInfo: ListInfoInput) {
    getActionOutputList(assetId: $assetId, listInfo: $listInfo) {
      output {
        actionId
        script {
          scriptId
          name
          custom
          language
        }
        statusMessage
        completedTime
        createdTime
        executionStatus
        isTestExecution
        policy {
          name
          policyId
          policySet {
            policySetId
            name
          }
        }
      }
      listInfo {
        page
        pageSize
        totalCount
        sort {
          attribute
          order
        }
      }
    }
  }
`;

const UPDATE_ASSET_OWNER = gql`
  mutation($assetId: ID!, $input: AssetInput!) {
    updateAsset(assetId: $assetId, input: $input) {
      assetId
      name
      owner {
        userId
      }
    }
  }
`;

const GET_ASSOCIATED_ASSETS = gql`
  query(
    $assetId: ID!
    $associatedEntityName: String!
    $listInfo: ListInfoInput!
  ) {
    getAssociatedAssetsV2(
      assetId: $assetId
      associatedEntityName: $associatedEntityName
      listInfo: $listInfo
    ) {
      assets {
        assetId
        name
        assetClass {
          classId
        }
        client {
          accountId
          name
        }
        site {
          id
          name
        }
        owner {
          userId
          name
        }
      }
      listInfo {
        page
        pageSize
        totalCount
      }
    }
  }
`;

const TRIGGER_ASSET_ACTION = gql`
  mutation($assetId: ID!, $input: AssetActionInput!) {
    triggerAssetAction(assetId: $assetId, input: $input)
  }
`;

const GET_ASSET_VIEW_META_INFO = gql`
  query {
    getAssetViewConditionMetaInfo
  }
`;

const TRIGGER_ASSET_ACTION_V2 = gql`
  mutation($assetId: ID!, $input: AssetActionInputV2!) {
    triggerAssetActionV2(assetId: $assetId, input: $input)
  }
`;

const GET_ASSET_FIREWALL_INPROGRESS_DETAILS = gql`
  query($assetId: ID!) {
    getAssetFireWallInProgressDetails(assetId: $assetId)
  }
`;

const ADD_ASSET_VIEW = gql`
  mutation($input: ListViewInput!) {
    addAssetView(input: $input) {
      id
      name
      viewCondition
      shared
      viewSubType
    }
  }
`;

const GET_ASSET_VIEW = gql`
  query($viewID: ID!) {
    getAssetView(viewID: $viewID) {
      id
      name
      viewCondition
      shared
      viewSubType
    }
  }
`;

const GET_VIEW = gql`
  query($viewID: ID!, $module: String!) {
    getView(module: $module, viewId: $viewID) {
      id
      name
      viewCondition
      shared
      viewSubType
    }
  }
`;

const UPDATE_ASSET_VIEW = gql`
  mutation($viewID: ID!, $input: ListViewInput!) {
    updateAssetView(viewID: $viewID, input: $input) {
      id
      name
    }
  }
`;

const DELETE_ASSET_VIEW = gql`
  mutation($viewID: ID!) {
    deleteAssetView(viewID: $viewID)
  }
`;

const GET_ASSET_VIEWS = gql`
  query {
    getAssetViews {
      id
      name
      viewCondition
      systemViewProperty
      shared
      customView
    }
  }
`;

const SOFT_DELETE_ASSET = gql`
  mutation($assetId: ID!) {
    softDeleteAsset(assetId: $assetId)
  }
`;

const RESTORE_ASSET = gql`
  mutation($assetIds: [ID!]) {
    restoreAssetsV2(assetIds: $assetIds) {
      status
      successCount
      failureCount
    }
  }
`;

const RESTORE_ASSET_V3 = gql`
  mutation($input: SelectionInput!) {
    restoreAssetsV3(input: $input)
  }
`;

const SOFT_DELETE_ASSETS = gql`
  mutation($assetIds: [ID!]) {
    softDeleteAssets(assetIds: $assetIds) {
      status
      successCount
      failureCount
    }
  }
`;

const SOFT_DELETE_ASSETS_V2 = gql`
  mutation($input: SoftDeleteAssetsInput!) {
    softDeleteAssetsV2(input: $input)
  }
`;

const HARD_DELETE_ASSET = gql`
  mutation($assetIds: [ID!]) {
    hardDeleteAssetsV2(assetIds: $assetIds) {
      status
      successCount
      failureCount
    }
  }
`;

const HARD_DELETE_ASSET_V3 = gql`
  mutation($input: SelectionInput!) {
    hardDeleteAssetsV3(input: $input)
  }
`;

const GET_NON_MONITORED_ASSET_META_INFO_LIST_V2 = gql`
  query($classId: ID) {
    getNonMonitoredAssetMetaInfoV2(classId: $classId)
  }
`;

const GET_NON_MONITORED_ASSET_META_INFO_LIST_V3 = gql`
  query($classId: ID) {
    getNonMonitoredAssetMetaInfoV3(classId: $classId)
  }
`;

const GET_COMMUNITY_SCRIPT_VIEW_META = gql`
  query {
    getCommunityScriptViewMetaInfo
  }
`;

const GET_SOFTWARE_BUNDLE = gql`
  query getThirdPartyApplicationBundle($id: ID!) {
    getThirdPartyApplicationBundle(id: $id) {
      id
      name
      assetClassId
      applications {
        id
        applicationReferenceId
        title
        name
        author
        iconUrl
        applicationUrl
        thirdPartyPackageManager {
          pmId
          packageManager
        }
        packageType
        status
        additionalIdentifier
      }
    }
  }
`;
const GET_SOFTWARE_BUNDLE_LIST = gql`
  query getThirdPartyApplicationBundleList($assetClassId: ID) {
    getThirdPartyApplicationBundleList(assetClassId: $assetClassId) {
      id
      name
      assetClassId
      applications {
        name
        patchOptions
        id
        applicationReferenceId
        title
        author
        iconUrl
        applicationUrl
        thirdPartyPackageManager {
          pmId
          packageManager
        }
        packageType
        status
      }
    }
  }
`;

const GET_THIRD_PART_PACKAGE_MANAGERS = gql`
  query getThirdPartyApplicationTabDetails($classId: ID!) {
    getThirdPartyApplicationTabDetails(classId: $classId)
  }
`;

const GET_THIRD_PART_APPLICATION_LIST = gql`
  query getThirdPartyApplicationList(
    $listInfo: ListInfoInput!
    $packageManagerID: Int
    $assetClassId: ID
  ) {
    getThirdPartyApplicationList(
      listInfo: $listInfo
      packageManagerId: $packageManagerID
      assetClassId: $assetClassId
    ) {
      thirdPartyApplications {
        id
        title
        name
        author
        iconUrl
        applicationUrl
        patchOptions
        latestVersion
        packageType
        status
        thirdPartyPackageManager {
          pmId
          packageManager
        }
      }
      listInfo {
        page
        pageSize
        sort {
          attribute
          order
        }
        totalCount
        hasMore
      }
    }
  }
`;

const GET_THIRD_PART_APPLICATION_LIST_ONDEMAND = gql`
  query getThirdPartyApplicationListOnDemand($searchTerm: String!) {
    getThirdPartyApplicationListOnDemand(searchTerm: $searchTerm) {
      thirdPartyApplications {
        id
        title
        author
        iconUrl
        applicationUrl
      }
      listInfo {
        page
        pageSize
        sort {
          attribute
          order
        }
        totalCount
        hasMore
      }
    }
  }
`;

const CREATE_SOFTWARE_BUNDLE = gql`
  mutation addThirdPartyApplicationBundleV2(
    $assetClassId: ID
    $bundleInput: ThirdPartyApplicationBundleInputV2!
  ) {
    addThirdPartyApplicationBundleV2(
      assetClassId: $assetClassId
      bundleInput: $bundleInput
    ) {
      id
      name
      applications {
        id
        applicationReferenceId
        title
        author
        applicationUrl
        iconUrl
      }
    }
  }
`;

const UPDATE_SOFTWARE_BUNDLE = gql`
  mutation updateThirdPartyApplicationBundleV2(
    $id: ID
    $bundleInput: ThirdPartyApplicationBundleInputV2!
  ) {
    updateThirdPartyApplicationBundleV2(id: $id, bundleInput: $bundleInput) {
      id
      name
      applications {
        id
        applicationReferenceId
        title
        author
        applicationUrl
        iconUrl
      }
    }
  }
`;

const DELETE_SOFTWARE_BUNDLE = gql`
  mutation deleteThirdPartyApplicationBundle($id: ID!) {
    deleteThirdPartyApplicationBundle(id: $id)
  }
`;

const GET_SOFTWARE_MANAGEMENT_POLICY_SET = gql`
  query($policyId: ID!) {
    getPolicy(policyId: $policyId) {
      policyId
      name
      description
      enabled
      thirdPartyApplicationConfig {
        scheduleDetail
        bundleConfigs {
          bundle {
            id
            name
          }
          applicationConfigs {
            application {
              name
              id
              applicationReferenceId
              iconUrl
              title
              author
              patchOptions
              thirdPartyPackageManager {
                pmId
                packageManager
              }
              packageType
              status
            }
            patchType
          }
        }
        applicationConfigs {
          application {
            id
            name
            applicationReferenceId
            title
            author
            iconUrl
            applicationUrl
            patchOptions
            packageType
            status
            thirdPartyPackageManager {
              pmId
              packageManager
            }
            additionalIdentifier
          }
          patchType
        }
      }
      mdmAppleSoftwareConfig {
        installIfVPPLicenseExpired
        installWithoutVPP
      }
      patchRebootConfig {
        rebootOnActiveSession
        rebootTriggerWindowInterval
        rebootOnInactiveSession
        rebootPermissionConfig
        customizedRebootMessage
        activeRebootWindowInterval
      }
      patchConfiguration {
        deployPatchOnAssetOnboard
        manageExistingSoftware
      }
    }
  }
`;

const UPDATE_POLICY_SET_SOFT_MGMT = gql`
  mutation(
    $policySetId: ID!
    $category: PolicySetCategory!
    $input: PolicySetInput!
    $clientId: ID
    $siteId: ID
    $assetId: ID
  ) {
    updatePolicySet(
      policySetId: $policySetId
      category: $category
      input: $input
      clientId: $clientId
      siteId: $siteId
      assetId: $assetId
    ) {
      policySetId
    }
  }
`;
const UPDATE_USER_ACTION_CONFIG = gql`
  mutation($assetId: ID!, $actionConfigId: ID!, $input: UserActionConfigInput) {
    updateUserActionConfig(
      assetId: $assetId
      actionConfigId: $actionConfigId
      input: $input
    ) {
      actionConfigId
      cancelled
    }
  }
`;

const DELETE_IN_PROGRESS_ACTION = gql`
  mutation($assetId: ID!, $actionIdList: [ID!]) {
    deleteInProgressAction(assetId: $assetId, actionIdList: $actionIdList)
  }
`;

const SOFTWARE_POLICY_ADD = gql`
  mutation updatePolicySet(
    $policySetId: ID!
    $category: PolicySetCategory!
    $input: PolicySetInput!
    $clientId: ID
    $siteId: ID
    $assetId: ID
  ) {
    updatePolicySet(
      policySetId: $policySetId
      category: $category
      input: $input
      clientId: $clientId
      siteId: $siteId
      assetId: $assetId
    ) {
      policySetId
    }
  }
`;

const SOFTWARE_POLICY_UPDATE = gql`
  mutation updatePolicySet(
    $policySetId: ID!
    $category: PolicySetCategory!
    $input: PolicySetInput!
    $clientId: ID
    $siteId: ID
    $assetId: ID
  ) {
    updatePolicySet(
      policySetId: $policySetId
      category: $category
      input: $input
      clientId: $clientId
      siteId: $siteId
      assetId: $assetId
    ) {
      policySetId
    }
  }
`;

const GET_ASSET_POLICIES = gql`
  query($assetId: ID) {
    getAssetPoliciesByPolicySet(assetId: $assetId) {
      policySetId
      name
      policyAssociations {
        policyType
        policies {
          policyId
          name
          enabled
          ... on AssetPolicy {
            inherited
            overridden
          }
        }
      }
    }
  }
`;

const GET_MANAGED_SOFTWARE = gql`
  query($assetId: ID!, $listInfo: ListInfoInput!) {
    getAssetThirdPartyApplicationsList(assetId: $assetId, listInfo: $listInfo) {
      assetThirdPartyApplications {
        thirdPartyApplication {
          name
          id
          applicationReferenceId
          title
          author
          latestVersion
          iconUrl
          thirdPartyPackageManager {
            pmId
            packageManager
          }
          packageType
        }
        errorCode
        errorStatusInfo
        status
        currentVersion
        configuredInPolicy
        updateAvailable
        installedTime
        lastUpdatedTime
        lastManagedBy
      }
      listInfo {
        page
        pageSize
        hasMore
        totalCount
        condition
        sort {
          attribute
          order
        }
      }
    }
  }
`;

const UPDATE_ASSET_CLIENT_AND_SITE = gql`
  mutation($assetIds: [ID!], $clientId: ID, $siteId: ID!) {
    updateAssetsClientAndSite(
      assetIds: $assetIds
      clientId: $clientId
      siteId: $siteId
    )
  }
`;

const UPDATE_ASSET_CLIENT_AND_SITE_V2 = gql`
  mutation($input: UpdateAssetsClientSiteInput!) {
    updateAssetsClientAndSiteV2(input: $input)
  }
`;

const TRIGGER_THIRD_PARTY_APPLICATION_PATCH_ACTION = gql`
  mutation($assetId: ID!, $applicationId: ID!, $action: String!) {
    triggerAssetThirdPartyApplicationPatchAction(
      assetId: $assetId
      applicationId: $applicationId
      action: $action
    ) {
      thirdPartyApplication {
        id
        applicationReferenceId
        title
        author
        latestVersion
        iconUrl
      }
      status
      updateAvailable
    }
  }
`;

const ASSET_WAKE_ON_LAN = gql`
  mutation($assetId: ID!) {
    wakeOnLan(assetId: $assetId)
  }
`;

const ASSET_REBOOT = gql`
  mutation($assetId: ID!, $input: AssetActionInputV2!) {
    triggerAssetActionV2(assetId: $assetId, input: $input)
  }
`;

const RUN_LIVE_ACTION = gql`
  mutation($assetId: ID, $actionType: String, $input: JSON) {
    runLiveAction(assetId: $assetId, actionType: $actionType, input: $input)
  }
`;

const RUN_LIVE_ACTION_FOR_ASSETS = gql`
  mutation($assetIds: [ID!], $actionType: String, $input: JSON) {
    runLiveActionForAssets(
      assetIds: $assetIds
      actionType: $actionType
      input: $input
    ) {
      status
      successCount
      failureCount
    }
  }
`;

const RUN_LIVE_ACTION_FOR_ASSETS_V2 = gql`
  mutation($input: RunLiveActionInput!) {
    runLiveActionsForAssetsV2(input: $input)
  }
`;

const APPROVE_AND_INSTALL_ASSET_PATCHES = gql`
  mutation(
    $assetId: ID!
    $patchIds: [ID!]
    $approve: Boolean!
    $install: Boolean!
  ) {
    approveAndInstallAssetPatches(
      assetId: $assetId
      patchIds: $patchIds
      approve: $approve
      install: $install
    )
  }
`;
const GET_BITLOCKET_STATUS = gql`
  query($assetId: ID!) {
    getAssetBitlockerInfo(assetId: $assetId) {
      drive
      protStatus
      volumeStatus
      lockStatus
      encMethod
      recoveryKeys
    }
  }
`;

const GET_SOFTWARENAME_LIST = gql`
  query($listInfo: ListInfoInput!) {
    getAssetSoftwareNamesList(listInfo: $listInfo)
  }
`;

const GET_SOFTWARELIST_DATATABLE = gql`
  query($metricId: ID!, $listInfo: ListInfoInput!) {
    getSoftwareInventoryDataTable(metricId: $metricId, listInfo: $listInfo) {
      listInfo {
        page
        pageSize
        hasMore
        totalCount
        sort {
          attribute
          order
        }
      }
      assets {
        assetId
        assetSoftware {
          name
          manufacturer
        }
        version
        installedDate
        workStation {
          assetId
          name
          hostName
          client {
            name
          }
          site {
            name
            id
          }
          platformCategory
          platform
        }
      }
    }
  }
`;
const GET_SHARED_IT_DOCS_DETAILS = gql`
  query($assetId: ID!) {
    getITDocSharedDetails(assetId: $assetId) {
      mappingId
      clientSharedType
      userRoleSharedType
      siteSharedType
      site {
        id
        name
      }
      client {
        accountId
        name
      }
      roles {
        roleId
        name
      }
    }
  }
`;

const GET_SOFTWARE_META = gql`
  query {
    getAssetSoftwareInventoryMetaInfo
  }
`;

const GET_SHAREABLE_CLIENTS = gql`
  query($assetId: ID!) {
    getITDocShareableClients(assetId: $assetId) {
      accountId
      name
      accountType
    }
  }
`;
const GET_SHAREABLE_SITES = gql`
  query($assetId: ID!) {
    getITDocShareableSites(assetId: $assetId) {
      id
      name
    }
  }
`;
const UPDATE_IT_DOC_SHARED_DETAILS = gql`
  mutation($assetId: ID!, $input: ITDocSharedUpdateInput) {
    updateITDocSharedDetails(assetId: $assetId, input: $input) {
      mappingId
      clientSharedType
      userRoleSharedType
      siteSharedType
      site {
        id
        name
      }
      client {
        accountId
        name
      }
      roles {
        roleId
        name
      }
    }
  }
`;
const GET_BIT_LOCKER_STATUSES = gql`
  query {
    getAssetBitLockerStatuses
  }
`;
const GET_PATCH_STATUSES = gql`
  query {
    getAssetPatchStatusMetaInfo
  }
`;

const GET_UPDATE_AVAILABLE_SOFTWARES = gql`
  query($assetId: ID!) {
    getUpdateAvailableSoftwares(assetId: $assetId) {
      thirdPartyApplication {
        id
        applicationReferenceId
        latestVersion
        title
        author
        summary
        description
        dependencies
        iconUrl
        applicationUrl
        packageStatus
        publishedAt
        packageApprovedDate
        lastUpdatedTime
        lastSyncedTime
      }
      status
      updateAvailable
    }
  }
`;

const TRIGGER_BULK_SOFTWARE_UPDATE = gql`
  mutation($assetId: ID!) {
    triggerBulkSoftwareUpdate(assetId: $assetId) {
      thirdPartyApplication {
        id
        applicationReferenceId
        latestVersion
        title
        author
        summary
        description
        dependencies
        iconUrl
        applicationUrl
        packageStatus
        publishedAt
        packageApprovedDate
        lastUpdatedTime
        lastSyncedTime
      }
      status
      updateAvailable
    }
  }
`;

const GET_ASSET_ACTIONS = gql`
  query($assetId: ID!) {
    getAssetIntegrationActions(assetId: $assetId)
  }
`;
const EXECUTE_ASSETS_ACTION = gql`
  mutation(
    $assetIds: [ID!]
    $integrationId: ID
    $action: String
    $input: JSON
  ) {
    executeAssetIntegrationAction(
      assetIds: $assetIds
      integrationId: $integrationId
      action: $action
      params: $input
    )
  }
`;

const GET_MONITORED_ASSET_META_INFO = gql`
  query(
    $viewType: String
    $version: Int
    $viewModule: String
    $assetClassId: ID
  ) {
    getMonitoredAssetsMetaInfo(
      viewType: $viewType
      version: $version
      viewModule: $viewModule
      assetClassId: $assetClassId
    )
  }
`;

const GET_ASSETS_FILTER = gql`
  query($viewType: String!, $listInfo: ListInfoInput) {
    getAllAssets(listInfo: $listInfo, viewType: $viewType) {
      assets {
        assetId
        name
        assetClass {
          classId
          name
          iconClass
        }
        owner {
          name
          userId
        }
        workstationInfo {
          teamViewerIntegrated
          splashtopIntegrated
          islIntegrated
          cwcIntegrated
          remoteDesktopEnabledStatus
          assetUserLoggedIn {
            userId
            userName
          }
        }
      }
      listInfo {
        page
        pageSize
        condition
        totalCount
        sort {
          attribute
          order
        }
        hasMore
      }
    }
  }
`;

const GET_MONITORED_ASSETS_IN_CONTRACT = gql`
  query($listInfo: ListInfoInput!, $viewType: String) {
    getMonitoredAssets(listInfo: $listInfo, viewType: $viewType) {
      assets {
        assetId
        name
        assetClass {
          classId
          name
        }
      }
      listInfo {
        page
        pageSize
        totalCount
        hasMore
      }
    }
  }
`;

const GET_ALL_ASSETS = gql`
  query($listInfo: ListInfoInput!, $viewType: String!) {
    getAllAssets(listInfo: $listInfo, viewType: $viewType) {
      assets {
        assetId
        name
        monitoringStatus
        createdTime
        updatedTime
        assetClass {
          classId
          name
          iconClass
        }
        workstationInfo {
          teamViewerIntegrated
          splashtopIntegrated
          cwcIntegrated
          remoteDesktopEnabledStatus
          assetUserLoggedIn {
            userId
            userName
          }
        }
        client {
          accountId
          name
        }
        site {
          id
          name
        }
        owner {
          userId
          name
        }
        department {
          name
          businessFunctionId
        }
        policyCategory {
          name
        }
      }
      listInfo {
        page
        pageSize
        condition
        totalCount
        sort {
          attribute
          order
        }
        hasMore
      }
    }
  }
`;

const GET_MONITORED_ASSETS = gql`
  query($listInfo: ListInfoInput!, $viewType: String, $assetClassId: ID) {
    getMonitoredAssets(
      listInfo: $listInfo
      viewType: $viewType
      assetClassId: $assetClassId
    ) {
      assets {
        assetId
        name
        assetClass {
          classId
          name
          iconClass
        }
        client {
          accountId
          name
        }
        site {
          id
          name
        }
        owner {
          userId
          name
        }
        ... on IPAsset {
          __typename
          status
          ipAddress
          macAddress
          publicIpAddress
          serialNumber
          hostName
          sysUptime
          assetMake {
            makeId
            name
          }
          assetModel {
            modelId
            name
          }
          policyCategory {
            name
          }
        }
      }
      listInfo {
        page
        pageSize
        condition
        totalCount
        sort {
          attribute
          order
        }
        hasMore
      }
    }
  }
`;

const ASSET_TIME_SERIES = gql`
  query getTimeseriesData($input: TimeseriesGraphInput) {
    getTimeseriesData(input: $input) {
      time
      metricSeries {
        metricId
        metricIdx
        data
        name
      }
      meta {
        unit
        name
      }
    }
  }
`;

const REVOKE_ASSET_PROBE_STATUS = gql`
  mutation($assetId: ID!) {
    revokeAssetProbeStatus(assetId: $assetId)
  }
`;
const GENERATE_INSTALLERS = gql`
  mutation($siteId: ID!) {
    generateInstallers(siteId: $siteId) {
      id
      installerName
      installerDownloadUrl
      os
      archPlaceholder
      installerCommandDetails
    }
  }
`;

const GET_ASSET_TYPES = gql`
  query getAssetViewTypes {
    getAssetViewTypes
  }
`;

const OVERRIDE_ASSET_POLICY_SETTINGS = gql`
  mutation overrideAssetAdvancedPolicySettings(
    $assetId: ID!
    $policyCategoryName: String!
    $policySetName: String!
  ) {
    overrideAssetAdvancedPolicySettings(
      assetId: $assetId
      policyCategoryName: $policyCategoryName
      policySetName: $policySetName
    ) {
      policySetId
      name
      category
    }
  }
`;

// create softeare meta
const GET_SOFTWARE_CREATE_META = gql`
  query($version: ID) {
    getCustomSoftwareMeta(version: $version)
  }
`;

// create CustomSoftwareID
const CREATE_CUST_SOFTWARE_ID = gql`
  mutation {
    createCustomSoftwareId
  }
`;

// save custom software info
const SAVE_CUSTOM_SOFTWARE = gql`
  mutation addCustomSoftware($customSoftwareInput: CustomSoftwareInput!) {
    addCustomSoftware(customSoftwareInput: $customSoftwareInput) {
      id
    }
  }
`;

const UPDATE_CUSTOM_SOFTWARE = gql`
  mutation updateCustomSoftware($customSoftwareInput: CustomSoftwareInput!) {
    updateCustomSoftware(customSoftwareInput: $customSoftwareInput) {
      id
    }
  }
`;

const FETCH_CUSTOM_SOFTWARE_LIST = gql`
  query getCustomSoftwareList($listInfo: ListInfoInput!) {
    getCustomSoftwareList(listInfo: $listInfo) {
      customSoftwareList {
        id
        title
        assetClass {
          name
          classId
          iconClass
        }
        iconUrl
        createdDate
        author {
          name
          avatarFileName
        }
        status
      }
      listInfo {
        page
        pageSize
        sort {
          attribute
          order
        }
        totalCount
        hasMore
      }
    }
  }
`;

const GET_CUSTOM_SOFTWARE = gql`
  query getCustomSoftware($id: ID) {
    getCustomSoftware(id: $id) {
      id
      title
      description
      assetClass {
        name
        classId
        iconClass
      }
      architecture
      installPatch {
        id
        patchType
        url
        parameters
        customSoftwareFileDetail {
          fileId
          fileName
          status
        }
        script {
          scriptId
          name
        }
        scriptArguments
        patchFileFormat
        preScript {
          scriptId
          name
        }
        preScriptArguments
        postScript {
          scriptId
          name
        }
        postScriptArguments
        additionalFiles {
          fileId
          fileName
          status
        }
      }
      updatePatchSameAsInstallPatch
      updatePatch {
        id
        patchType
        url
        parameters
        customSoftwareFileDetail {
          fileId
          fileName
          status
        }
        script {
          scriptId
          name
        }
        scriptArguments
        patchFileFormat
        preScript {
          scriptId
          name
        }
        preScriptArguments
        postScript {
          scriptId
          name
        }
        postScriptArguments
        additionalFiles {
          fileId
          fileName
          status
        }
      }
      uninstallPatch {
        id
        patchType
        url
        parameters
        customSoftwareFileDetail {
          fileId
          fileName
          status
        }
        script {
          scriptId
          name
        }
        scriptArguments
        patchFileFormat
        preScript {
          scriptId
          name
        }
        preScriptArguments
        postScript {
          scriptId
          name
        }
        postScriptArguments
        additionalFiles {
          fileId
          fileName
          status
        }
      }
      iconUrl
      iconPath
      runAs
    }
  }
`;
const DELETE_CUSTOM_SOFTWARE = gql`
  mutation deleteCustomSoftware($id: ID) {
    deleteCustomSoftware(id: $id)
  }
`;
const GET_TIME_SERIES_FILTER_META_INFO = gql`
  query GetTimeSeriesMetaInfo($metricId: ID!, $entityId: ID!) {
    getTimeSeriesMetaInfo(metricId: $metricId, entityId: $entityId)
  }
`;
const SOFTWARE_LIST_VIEW_META = gql`
  query($version: ID, $pmId: ID, $tabId: String) {
    getThirdPartySoftwareMeta(version: $version, pmId: $pmId, tabId: $tabId)
  }
`;

const ADHOC_MANAGE_THIRD_PARTY_SOFTWARE = gql`
  mutation adhocManageThirdPartySoftware(
    $assetId: ID!
    $softwareInput: AdhocSoftwareInput!
  ) {
    adhocManageThirdPartySoftware(
      assetId: $assetId
      softwareInput: $softwareInput
    )
  }
`;

const HARD_DELETE_ASSET_CLASS_INACTIVE_FIELDS = gql`
  mutation hardDeleteAssetClassInactiveFields(
    $classId: ID
    $commonWorkStationField: Boolean
    $columnGroup: String
  ) {
    hardDeleteAssetClassInactiveFields(
      classId: $classId
      columnGroup: $columnGroup
      commonWorkStationField: $commonWorkStationField
    )
  }
`;

const GENERATE_TUNNEL_TOKEN = gql`
  mutation generateTunnelToken($assetId: ID!, $targetUrl: String!) {
    generateTunnelToken(assetId: $assetId, targetUrl: $targetUrl) {
      remotingUrl
    }
  }
`;

const TERMINATE_CONNECTION = gql`
  mutation terminateConnection($connectionId: ID!) {
    terminateConnection(connectionId: $connectionId)
  }
`;

const EXISTING_REMOTE_CONNECTION_DETAILS_FOR_ASSET = gql`
  mutation existingRemoteConnectionDetailsForAsset(
    $assetId: ID!
    $sessionType: [SessionType]
  ) {
    existingRemoteConnectionDetailsForAsset(
      assetId: $assetId
      sessionType: $sessionType
    ) {
      userDetails {
        userId
        userName
      }
      connectionDetails {
        id
        url
        sessionTypes
        startTime
      }
    }
  }
`;

const GET_REMOTING_INSTALLERS = gql`
  mutation GetRemotingInstallers {
    getRemotingInstallers {
      remotingInstallerPackage {
        installerDownloadUrl
        os
        arch
      }
    }
  }
`;
const GET_APPLE_STORE_FRONTS = gql`
  query getAppleStoreFronts($classId: ID) {
    getAppleStoreFronts(classId: $classId) {
      displayName
      id
    }
  }
`;

const GET_VPP_APPLICATION_LIST = gql`
  query getVPPApplicationList($assetClassId: ID, $listInfo: ListInfoInput) {
    getVPPApplicationList(assetClassId: $assetClassId, listInfo: $listInfo) {
      apps {
        id
        adamId
        title
        author
        iconUrl
        applicationUrl
        patchOptions
        supportedAssetClass {
          classId
          name
        }
        totalLicense
        availableLicense
        bundleId
      }
      listInfo {
        page
        pageSize
        totalCount
        sort {
          attribute
          order
        }
      }
    }
  }
`;

const SYNC_APP_STORE_APPS = gql`
  mutation syncAppStoreApps($input: [AppleSoftware!]!) {
    syncAppStoreApps(input: $input) {
      id
      applicationReferenceId
      latestVersion
      title
      name
      author
      iconUrl
      applicationUrl
      packageStatus
      packageType
      lastUpdatedTime
      lastSyncedTime
      patchOptions
      thirdPartyPackageManager {
        pmId
        packageManager
        iconClass
      }
      status
      additionalIdentifier
    }
  }
`;

const GET_APP_STORE_APPLICATION_LIST = gql`
  query getAppStoreApplicationList(
    $searchTerm: String
    $countryCode: String
    $classId: ID
  ) {
    getAppStoreApplicationList(
      searchTerm: $searchTerm
      countryCode: $countryCode
      classId: $classId
    ) {
      id
      adamId
      title
      author
      iconUrl
      applicationUrl
      supportedAssetClass {
        classId
        name
      }
      patchOptions
      totalLicense
      availableLicense
      bundleId
    }
  }
`;

const GET_ANDROID_PLAY_STORE_WEB_TOKEN = gql`
  query getAndroidPlayStoreWebToken($frameUrl: String) {
    getAndroidPlayStoreWebToken(frameUrl: $frameUrl) {
      token
    }
  }
`;

const SYNC_PLAY_STORE_APP = gql`
  mutation syncPlayStoreApp($packageName: String!) {
    syncPlayStoreApp(packageName: $packageName) {
      id
      name
      title
      author
      iconUrl
      applicationUrl
      lastUpdatedTime
      applicationReferenceId
      patchOptions
      thirdPartyPackageManager {
        pmId
      }
    }
  }
`;

export {
  GET_ASSET_ACTIONS,
  EXECUTE_ASSETS_ACTION,
  GET_ASSET_LIST,
  GET_ASSET,
  GET_ASSET_ATTACHMENTS,
  GET_NON_MONITORED_ASSET_ATTACHMENTS,
  GET_ASSET_TOP_CPU_PROCESS,
  GET_ASSET_CPU_DETAILS,
  GET_ASSET_TOP_MEMORY_PROCESS,
  GET_ASSET_DISK_DETAILS,
  GET_ASSET_USER_LOG,
  GET_ASSET_OPEN_PORTS,
  GET_ASSET_SERVICE,
  GET_ASSET_EVENTS,
  GET_ASSET_INTERFACE,
  GET_ASSET_HARDWARE_AND_MISC,
  GET_ASSET_SOFTWARES,
  GET_ASSET_SUMMARY,
  GET_ALL_ASSET_CLASS,
  GET_ASSET_GROUP_META,
  GET_POLICIES_BY_CRITERIA,
  GET_POLICY_TYPES,
  ADD_ASSET_GROUP,
  UPDATE_ASSET_GROUP,
  GET_ASSET_GROUP,
  DELETE_ASSET_GROUP,
  GET_ASSET_GROUP_LIST,
  GET_ASSET_GROUP_VIEW_LIST,
  GET_NON_MONITORED_ASSET_CLASS_LIST,
  GET_ENTITY_META,
  CREATE_ASSET,
  UPDATE_ASSET,
  GET_ACTIVITY_FOR_ASSET,
  GET_TICKETS_FOR_ASSET,
  GET_ALERTS_FOR_ASSET,
  GET_PATCHES_FOR_ASSET,
  GET_ASSET_META_INFO,
  GET_ASSET_PATCH_STATUS,
  GET_ASSET_CLASS,
  GET_ASSET_CLASS_FOR_ASSET,
  ASSOCIATE_NON_MONITORED_ASSET,
  CREATE_USER_ACTION_CONFIG,
  REMOVE_ASSOCIATION,
  DELETE_NON_MONITORED_ASSET,
  GET_ASSET_LIST_TO_ASSOCIATE,
  GET_ASSET_LIST_TO_ASSOCIATE_V2,
  GET_ASSET_UPCOMING_SCRIPTS,
  GET_EXECUTED_SCRIPTS,
  GET_ASSET_MEMORY_DETAILS,
  GET_ASSET_CLASS_LIST,
  UPDATE_ASSET_OWNER,
  GET_ASSOCIATED_ASSETS,
  GET_ANTIVIRUS_FOR_ASSET,
  GET_ASSET_CLASS_V1,
  WORKSTATION_FIELDS,
  GET_WORK_STATION_META_INFO_V4,
  TRIGGER_ASSET_ACTION,
  GET_ASSET_VIEW_META_INFO,
  ADD_ASSET_VIEW,
  GET_ASSET_VIEW,
  UPDATE_ASSET_VIEW,
  DELETE_ASSET_VIEW,
  GET_ASSET_VIEWS,
  GET_CLASS_ID_FOR_ASSET,
  GET_ASSET_GROUP_BY_ASSET_CLASS,
  GET_ASSET_GROUP_INFO_BY_CLASSID,
  SOFT_DELETE_ASSET,
  RESTORE_ASSET,
  RESTORE_ASSET_V3,
  HARD_DELETE_ASSET,
  HARD_DELETE_ASSET_V3,
  GET_WORKSTATION_LIST,
  GET_ASSET_ALL_PROCESS,
  GET_ASSET_LIST_V2,
  GET_ASSET_META_INFO_V4,
  GET_ASSOCIATED_ASSET_CLASS_LIST_FOR_ASSET,
  GET_NON_MONITORED_ASSET_META_INFO_LIST_V2,
  GET_NON_MONITORED_ASSET_META_INFO_LIST_V3,
  GET_ASSET_ACRONIS_STATUS,
  GET_PATCHES_LIST,
  TRIGGER_ASSET_ACTION_V2,
  GET_ASSET_FIREWALL_INPROGRESS_DETAILS,
  GET_COMMUNITY_SCRIPT_VIEW_META,
  GET_SOFTWARE_BUNDLE,
  GET_SOFTWARE_BUNDLE_LIST,
  GET_THIRD_PART_APPLICATION_LIST,
  GET_THIRD_PART_APPLICATION_LIST_ONDEMAND,
  CREATE_SOFTWARE_BUNDLE,
  UPDATE_SOFTWARE_BUNDLE,
  DELETE_SOFTWARE_BUNDLE,
  GET_SOFTWARE_MANAGEMENT_POLICY_SET,
  UPDATE_POLICY_SET_SOFT_MGMT,
  UPDATE_USER_ACTION_CONFIG,
  SOFTWARE_POLICY_ADD,
  SOFTWARE_POLICY_UPDATE,
  GET_ASSET_POLICIES,
  GET_MANAGED_SOFTWARE,
  UPDATE_ASSET_CLIENT_AND_SITE,
  UPDATE_ASSET_CLIENT_AND_SITE_V2,
  TRIGGER_THIRD_PARTY_APPLICATION_PATCH_ACTION,
  GET_ASSET_PATCH_SCHEDULE_ACTION_INFO,
  ASSET_WAKE_ON_LAN,
  ASSET_REBOOT,
  APPROVE_AND_INSTALL_ASSET_PATCHES,
  GET_ASSET_SERVICE_REDIS,
  GET_BITLOCKET_STATUS,
  RUN_LIVE_ACTION,
  BULK_CREATE_USER_ACTION_CONFIG,
  BULK_CREATE_USER_ACTION_CONFIG_V2,
  GET_ASSET_HARDWARE_AND_MISC_MAC,
  GET_SOFTWARENAME_LIST,
  GET_SOFTWARELIST_DATATABLE,
  GET_SOFTWARE_META,
  GET_SHARED_NON_MONITORED_ASSET_CLASS_LIST,
  GET_ALL_SHARED_IT_DOCUMENTS,
  GET_SHARED_ASSET_CLASS,
  GET_SHARED_IT_DOCS_DETAILS,
  GET_SHAREABLE_CLIENTS,
  GET_SHAREABLE_SITES,
  GET_NON_MONITOREDASSETLIST,
  GET_NON_MONITORED_ASSET_CLASS,
  GET_NON_MONITORED_ASSET_LIST_TO_ASSOCIATE,
  UPDATE_IT_DOC_SHARED_DETAILS,
  SOFT_DELETE_ASSETS,
  SOFT_DELETE_ASSETS_V2,
  GET_BIT_LOCKER_STATUSES,
  GET_PATCH_STATUSES,
  GET_UPDATE_AVAILABLE_SOFTWARES,
  TRIGGER_BULK_SOFTWARE_UPDATE,
  RUN_LIVE_ACTION_FOR_ASSETS,
  RUN_LIVE_ACTION_FOR_ASSETS_V2,
  GET_MONITORED_ASSET_META_INFO,
  GET_MONITORED_ASSETS,
  ASSET_TIME_SERIES,
  GET_ASSETS_FILTER,
  REVOKE_ASSET_PROBE_STATUS,
  GET_MONITORED_ASSET_CLASS_LIST,
  GENERATE_INSTALLERS,
  GET_ASSET_GROUP_V2,
  GET_ASSET_TYPES,
  GET_VIEW,
  GET_MONITORED_ASSETS_IN_CONTRACT,
  GET_ALL_ASSETS,
  GET_CLASS_FOR_ASSET_DETAILS,
  GET_ASSET_CLASS_FOR_NON_MONITOR_ASSET,
  DELETE_IN_PROGRESS_ACTION,
  GET_WORKSTATION_ASSET_CLASS_LIST,
  GET_THIRD_PART_PACKAGE_MANAGERS,
  OVERRIDE_ASSET_POLICY_SETTINGS,
  GET_SOFTWARE_CREATE_META,
  CREATE_CUST_SOFTWARE_ID,
  SAVE_CUSTOM_SOFTWARE,
  FETCH_CUSTOM_SOFTWARE_LIST,
  GET_CUSTOM_SOFTWARE,
  UPDATE_CUSTOM_SOFTWARE,
  DELETE_CUSTOM_SOFTWARE,
  GET_TIME_SERIES_FILTER_META_INFO,
  GET_BATTERY_DETAILS,
  SOFTWARE_LIST_VIEW_META,
  DELETE_SOFTWARE,
  ADHOC_MANAGE_THIRD_PARTY_SOFTWARE,
  GET_ASSET_DATA_BACKUP_DETAIL,
  HARD_DELETE_ASSET_CLASS_INACTIVE_FIELDS,
  GET_ASSET_CLASS_V1_CUSTOM_FIELDS,
  GENERATE_TUNNEL_TOKEN,
  TERMINATE_CONNECTION,
  EXISTING_REMOTE_CONNECTION_DETAILS_FOR_ASSET,
  GET_REMOTING_INSTALLERS,
  GET_APPLE_STORE_FRONTS,
  GET_VPP_APPLICATION_LIST,
  SYNC_APP_STORE_APPS,
  GET_APP_STORE_APPLICATION_LIST,
  GET_ANDROID_PLAY_STORE_WEB_TOKEN,
  SYNC_PLAY_STORE_APP,
  GET_TOTP_FIELD_VALUE,
  GET_ASSET_CLASS_LIST_INFO
};
